// Calendar meetings automatic matching couldn't assign to exactly one client.
// Inline resolution: confirm a suggested client, pick another, or mark the
// event as not a client meeting. Integrations is the canonical surface.
import { useCallback, useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { format, isToday, isTomorrow } from "date-fns";
import { Loader2, Search, ChevronLeft } from "lucide-react";
import { toast } from "sonner";

function whenLabel(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const t = format(d, "h:mm a");
  if (isToday(d)) return `Today · ${t}`;
  if (isTomorrow(d)) return `Tomorrow · ${t}`;
  return `${format(d, "EEE d MMM")} · ${t}`;
}

export default function NeedsReview({ refreshKey }) {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/meetings/review");
      setRows(data.meetings || []);
      setError(false);
    } catch { setError(true); setRows(r => r || []); }
  }, []);

  useEffect(() => { load(); }, [load, refreshKey]);

  if (rows === null) return null;
  if (error && rows.length === 0) {
    return (
      <p data-testid="needs-review-error" className="text-sm text-slate-500 mb-6">
        Couldn't load meetings that need review. <button onClick={load} className="underline underline-offset-2">Retry</button>
      </p>
    );
  }
  if (rows.length === 0) return null;

  return (
    <section id="needs-review" data-testid="needs-review"
             className="rounded-xl border border-slate-200 bg-white mb-6 overflow-hidden">
      <div className="px-6 py-4 border-b border-slate-100">
        <p className="overline mb-0.5">Needs review</p>
        <p data-testid="needs-review-count" className="font-medium text-slate-900">
          {rows.length === 1 ? "1 meeting needs a client match" : `${rows.length} meetings need a client match`}
        </p>
        <p className="text-sm text-slate-500 mt-0.5">
          These attendees belong to more than one client. Confirm who each meeting is with so it shows up on the right client.
        </p>
      </div>
      <ul className="divide-y divide-slate-100">
        {rows.map(m => (
          <ReviewRow key={m.meeting_id} meeting={m}
                     onResolved={(id) => setRows(r => r.filter(x => x.meeting_id !== id))} />
        ))}
      </ul>
    </section>
  );
}

function ReviewRow({ meeting, onResolved }) {
  const [busy, setBusy] = useState(null);       // client_id | "ignore" | null
  const [picking, setPicking] = useState(false);

  const resolve = async (payload, label) => {
    const key = payload.client_id || "ignore";
    setBusy(key);
    try {
      await api.post(`/meetings/${meeting.meeting_id}/match`, payload);
      toast.success(label);
      onResolved(meeting.meeting_id);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 404) {
        toast.error("This meeting is no longer available — it may have been cancelled or removed.");
        onResolved(meeting.meeting_id);
      } else toast.error("Couldn't save that. Please try again.");
    } finally { setBusy(null); }
  };

  const single = meeting.candidates.length === 1;

  return (
    <li data-testid={`review-row-${meeting.meeting_id}`} className="px-6 py-5">
      <div className="flex flex-wrap items-start justify-between gap-x-6 gap-y-3">
        <div className="min-w-0 flex-1 basis-64">
          <p data-testid="review-title" className="font-medium text-slate-900 truncate">{meeting.title || "Untitled meeting"}</p>
          <p className="text-xs text-slate-500 mt-0.5">{whenLabel(meeting.start)}</p>
          {meeting.attendees?.length > 0 && (
            <p className="text-xs text-slate-500 mt-1 break-words">
              <span className="text-slate-400">Attendees:</span> {meeting.attendees.slice(0, 4).join(", ")}
              {meeting.attendees.length > 4 && ` +${meeting.attendees.length - 4} more`}
            </p>
          )}
        </div>

        <div className="min-w-0 flex-1 basis-80">
          {picking ? (
            <ClientPicker
              busy={busy}
              onBack={() => setPicking(false)}
              onPick={(c) => resolve({ client_id: c.client_id }, `Meeting linked to ${c.company_name}`)}
            />
          ) : (
            <>
              <p className="overline mb-2">{single ? "Possible client" : "Possible clients"}</p>
              <ul className="space-y-2.5">
                {meeting.candidates.map(c => (
                  <li key={c.client_id} className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-slate-900 truncate">{c.company_name}</p>
                      <p className="text-xs text-slate-500 truncate">
                        {[c.contact_name, c.contact_email].filter(Boolean).join(" · ")}
                      </p>
                    </div>
                    <button data-testid={`review-confirm-${c.client_id}`}
                            onClick={() => resolve({ client_id: c.client_id }, `Meeting linked to ${c.company_name}`)}
                            disabled={!!busy} aria-label={`Confirm ${c.company_name}`}
                            className="text-xs font-medium text-white bg-[#C55A46] hover:bg-[#B4523E] disabled:bg-slate-300 rounded-full px-3 py-1.5 inline-flex items-center gap-1.5 transition-colors shrink-0">
                      {busy === c.client_id && <Loader2 className="w-3 h-3 animate-spin" />}
                      Confirm {single ? c.company_name : ""}
                    </button>
                  </li>
                ))}
              </ul>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-3">
                <button data-testid="review-choose-other" onClick={() => setPicking(true)} disabled={!!busy}
                        className="text-xs font-medium text-slate-700 hover:text-slate-900 underline-offset-2 hover:underline">
                  Choose another client
                </button>
                <button data-testid="review-ignore" disabled={!!busy}
                        onClick={() => resolve({ ignore: true }, "Marked as not a client meeting")}
                        className="text-xs text-slate-500 hover:text-slate-800 underline-offset-2 hover:underline inline-flex items-center gap-1.5">
                  {busy === "ignore" && <Loader2 className="w-3 h-3 animate-spin" />}
                  Not a client meeting
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </li>
  );
}

function ClientPicker({ onPick, onBack, busy }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => { inputRef.current?.focus(); }, []);

  useEffect(() => {
    if (!q.trim()) { setResults([]); return; }
    let cancelled = false;
    setSearching(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/clients/search?q=${encodeURIComponent(q.trim())}`);
        if (!cancelled) setResults(data.clients || []);
      } catch { if (!cancelled) setResults([]); }
      finally { if (!cancelled) setSearching(false); }
    }, 220);
    return () => { cancelled = true; clearTimeout(t); };
  }, [q]);

  return (
    <div data-testid="client-picker">
      <div className="flex items-center gap-2 mb-2">
        <button onClick={onBack} aria-label="Back to suggestions" data-testid="client-picker-back"
                className="text-slate-500 hover:text-slate-900 p-1 -ml-1 rounded">
          <ChevronLeft className="w-4 h-4" />
        </button>
        <p className="overline">Choose a client</p>
      </div>
      <div className="relative">
        <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
        <input ref={inputRef} value={q} onChange={e => setQ(e.target.value)} data-testid="client-picker-input"
               aria-label="Search clients" placeholder="Search by client, contact, email or project"
               className="w-full text-sm border border-slate-200 rounded-lg pl-9 pr-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#C55A46]/30 focus:border-[#C55A46]" />
        {searching && <Loader2 className="w-4 h-4 text-slate-400 animate-spin absolute right-3 top-1/2 -translate-y-1/2" />}
      </div>
      {q.trim() && !searching && results.length === 0 && (
        <p data-testid="client-picker-empty" className="text-xs text-slate-500 mt-2">No clients match "{q.trim()}".</p>
      )}
      {results.length > 0 && (
        <ul role="listbox" className="mt-2 border border-slate-200 rounded-lg divide-y divide-slate-100 max-h-56 overflow-auto">
          {results.map(c => (
            <li key={c.client_id}>
              <button role="option" aria-selected="false" data-testid={`client-picker-option-${c.client_id}`}
                      onClick={() => onPick(c)} disabled={!!busy}
                      className="w-full text-left px-3 py-2 hover:bg-slate-50 focus:bg-slate-50 focus:outline-none flex items-center justify-between gap-3">
                <span className="min-w-0">
                  <span className="block text-sm font-medium text-slate-900 truncate">{c.company_name}</span>
                  <span className="block text-xs text-slate-500 truncate">
                    {[c.contact_name, c.contact_email, c.project_name].filter(Boolean).join(" · ")}
                  </span>
                </span>
                {busy === c.client_id && <Loader2 className="w-3.5 h-3.5 animate-spin text-slate-400 shrink-0" />}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
